/**
 * Form Section Component
 * Groups related form fields with a title and optional description
 */

import React from "react";
import { Box, Typography, Divider, Card, CardContent } from "@mui/material";

export interface FormSectionProps {
  /** Section title */
  title: string;
  /** Section description */
  description?: string;
  /** Whether to show as a card */
  variant?: "default" | "card";
  /** Whether to show divider */
  showDivider?: boolean;
  /** Children (form fields) */
  children: React.ReactNode;
  /** Whether section is collapsible */
  collapsible?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Icon to show next to title */
  icon?: React.ReactNode;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  variant = "default",
  showDivider = true,
  children,
  icon,
}) => {
  const content = (
    <>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          {icon}
          <Typography variant="h6" sx={{
            fontWeight: 600
          }}>
            {title}
          </Typography>
        </Box>
        {description && (
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {description}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {children}
      </Box>

      {showDivider && variant === "default" && (
        <Divider sx={{ mt: 4, mb: 3 }} />
      )}
    </>
  );

  if (variant === "card") {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>{content}</CardContent>
      </Card>
    );
  }

  return <Box sx={{ mb: showDivider ? 0 : 4 }}>{content}</Box>;
};

export interface FormRowProps {
  /** Number of columns (1-4) */
  columns?: 1 | 2 | 3 | 4;
  /** Gap between items */
  gap?: number;
  /** Children (form fields) */
  children: React.ReactNode;
  /** Alignment */
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch";
}

/**
 * Form Row - Arranges form fields in a responsive grid
 */
export const FormRow: React.FC<FormRowProps> = ({
  columns = 2,
  gap = 3,
  children,
  alignItems = "flex-start",
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: columns > 2 ? "repeat(2, 1fr)" : `repeat(${columns}, 1fr)`,
          md: `repeat(${columns}, 1fr)`,
        },
        gap,
        alignItems,
      }}
    >
      {children}
    </Box>
  );
};

export interface FormActionsProps {
  /** Children (buttons) */
  children: React.ReactNode;
  /** Alignment */
  align?: "left" | "center" | "right" | "space-between";
  /** Whether to add top margin */
  withMargin?: boolean;
  /** Whether to show sticky at bottom */
  sticky?: boolean;
}

/**
 * Form Actions - Container for form submit/cancel buttons
 */
export const FormActions: React.FC<FormActionsProps> = ({
  children,
  align = "right",
  withMargin = true,
  sticky = false,
}) => {
  const justifyContent =
    align === "space-between"
      ? "space-between"
      : align === "left"
      ? "flex-start"
      : align === "center"
      ? "center"
      : "flex-end";

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent,
        mt: withMargin ? 4 : 0,
        ...(sticky && {
          position: "sticky",
          bottom: 0,
          bgcolor: "background.paper",
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          mx: -3,
          px: 3,
        }),
      }}
    >
      {children}
    </Box>
  );
};

export default FormSection;
